import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { Connector, SharePointFileSizeBucket, SharePointFileTypeCountItem, SharePointItem } from '../../core/services/connector';
import { Document, DocumentItem } from '../../core/services/document';

interface DonutSegment {
  label: string;
  percentage: number;
  color: string;
  count: number;
  offset: number;
  sizeBytes: number;
}

interface OtherTypeItem {
  fileType: string;
  label: string;
  count: number;
  percentage: number;
  share: number;
  sizeBytes: number;
}

interface TypeFilter {
  group: string;
  extension: string | null;
}

type AnalyticsTab = 'unprocessed' | 'failed' | 'processed';

const PAGE_SIZE = 10;
const API_PAGE_SIZE = 200;

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [CommonModule, Sidebar, Navbar, ToastComponent],
  templateUrl: './analytics.html',
  styleUrl: './analytics.css'
})
export class Analytics implements OnInit {
  private connectorService = inject(Connector);
  private documentService = inject(Document);
  private toast = inject(Toast);

  activeTab = signal<AnalyticsTab>('unprocessed');

  allUnprocessed = signal<SharePointItem[]>([]);
  unprocessedTotal = signal(0);
  unprocessedMatchTotal = signal(0);
  isLoadingUnprocessed = signal(true);
  searchQuery = signal('');
  currentPage = signal(1);

  isLoadingFailed = signal(false);
  failedLoaded = signal(false);
  allFailed = signal<DocumentItem[]>([]);
  failedSearchQuery = signal('');
  failedPage = signal(1);

  fileTypeCounts = signal<SharePointFileTypeCountItem[]>([]);
  fileTypeTotal = signal(0);
  isLoadingFileTypes = signal(true);

  isLoadingProcessed = signal(false);
  processedLoaded = signal(false);
  allProcessed = signal<DocumentItem[]>([]);
  processedSearchQuery = signal('');
  processedPage = signal(1);

  typeFilter = signal<TypeFilter | null>(null);
  othersExpanded = signal(false);
  sizeOthersExpanded = signal(false);
  readonly showSizeCharts = false;

  sizeUnprocessedByType = signal<SharePointFileSizeBucket[]>([]);
  sizeFailedByType = signal<SharePointFileSizeBucket[]>([]);
  sizeProcessedByType = signal<SharePointFileSizeBucket[]>([]);
  unprocessedBytes = signal(0);
  failedBytes = signal(0);
  processedBytes = signal(0);
  isLoadingFileSizes = signal(true);

  readonly pageSize = PAGE_SIZE;

  totalPages = computed(() => {
    const listed = Math.min(this.unprocessedMatchTotal(), 1000);
    return Math.max(1, Math.ceil(listed / this.pageSize));
  });
  pagedItems = computed(() => this.allUnprocessed());

  filteredFailed = computed(() => {
    const search = this.failedSearchQuery().trim().toLowerCase();
    const docs = this.applyTypeFilterToDocs(this.allFailed());
    if (!search) return docs;
    return docs.filter(d => d.fileName.toLowerCase().includes(search));
  });

  totalFailedPages = computed(() => Math.max(1, Math.ceil(this.filteredFailed().length / this.pageSize)));
  pagedFailed = computed(() => {
    const start = (this.failedPage() - 1) * this.pageSize;
    return this.filteredFailed().slice(start, start + this.pageSize);
  });

  filteredProcessed = computed(() => {
    const search = this.processedSearchQuery().trim().toLowerCase();
    const docs = this.applyTypeFilterToDocs(this.allProcessed());
    if (!search) return docs;
    return docs.filter(d => d.fileName.toLowerCase().includes(search));
  });

  totalProcessedPages = computed(() => Math.max(1, Math.ceil(this.filteredProcessed().length / this.pageSize)));
  pagedProcessed = computed(() => {
    const start = (this.processedPage() - 1) * this.pageSize;
    return this.filteredProcessed().slice(start, start + this.pageSize);
  });

  isChartLoading = computed(() => {
    if (this.activeTab() === 'unprocessed') return this.isLoadingFileTypes();
    if (this.activeTab() === 'failed') return this.isLoadingFailed();
    return this.isLoadingProcessed();
  });

  chartTotal = computed(() => {
    if (this.activeTab() === 'unprocessed') {
      const fromSegments = this.fileTypeSegments().reduce((sum, item) => sum + item.count, 0);
      return fromSegments > 0 ? fromSegments : this.fileTypeTotal();
    }
    if (this.activeTab() === 'failed') return this.allFailed().length;
    return this.allProcessed().length;
  });

  chartTitle = computed(() => {
    if (this.activeTab() === 'failed') return 'Failed file types';
    if (this.activeTab() === 'processed') return 'Processed file types';
    return 'SharePoint file types';
  });

  chartCaption = computed(() => {
    if (this.activeTab() === 'failed') return 'Failed SharePoint documents by major type. Expand Others to see remaining extensions.';
    if (this.activeTab() === 'processed') return 'Processed SharePoint documents by major type. Expand Others to see remaining extensions.';
    return 'Indexed library mix by major type. Expand Others to see remaining extensions.';
  });

  chartEmptyMessage = computed(() => {
    if (this.activeTab() === 'failed') return 'No failed documents to chart yet.';
    if (this.activeTab() === 'processed') return 'No processed documents to chart yet.';
    return 'No SharePoint file type counts yet.';
  });

  fileTypeSegments = computed<DonutSegment[]>(() => {
    const tab = this.activeTab();
    if (tab === 'unprocessed') {
      return this.segmentsFromCounts(this.fileTypeCounts(), this.fileTypeTotal());
    }
    const docs = tab === 'failed' ? this.allFailed() : this.allProcessed();
    return this.segmentsFromDocs(docs);
  });

  otherTypeBreakdown = computed<OtherTypeItem[]>(() => {
    const tab = this.activeTab();
    const rows = new Map<string, number>();

    if (tab === 'unprocessed') {
      for (const item of this.fileTypeCounts()) {
        if (this.majorFileTypeLabel(item.fileType) !== 'Others') continue;
        const key = this.normalizeExt(item.fileType) || 'other';
        rows.set(key, (rows.get(key) ?? 0) + (item.count || 0));
      }
    } else {
      const docs = tab === 'failed' ? this.allFailed() : this.allProcessed();
      for (const doc of docs) {
        const ext = this.normalizeExt(this.docExtension(doc));
        if (this.majorFileTypeLabel(ext) !== 'Others') continue;
        const key = ext || 'other';
        rows.set(key, (rows.get(key) ?? 0) + 1);
      }
    }

    const sorted = [...rows.entries()]
      .map(([fileType, count]) => ({ fileType, count }))
      .sort((a, b) => b.count - a.count || a.fileType.localeCompare(b.fileType));

    const denom = this.chartTotal();
    const othersSum = sorted.reduce((sum, row) => sum + row.count, 0);
    return sorted.map(row => ({
      fileType: row.fileType,
      label: this.otherTypeDisplay(row.fileType),
      count: row.count,
      percentage: denom > 0 ? Math.round((row.count / denom) * 1000) / 10 : 0,
      share: othersSum > 0 ? Math.round((row.count / othersSum) * 1000) / 10 : 0,
      sizeBytes: 0
    }));
  });

  othersTooltip = computed(() => {
    const rows = this.otherTypeBreakdown();
    if (!rows.length) return 'Others';
    const shown = rows.slice(0, 8).map(row => `${row.label} ${row.count}`);
    const extra = rows.length - shown.length;
    const detail = extra > 0 ? `${shown.join(', ')}, +${extra} more` : shown.join(', ');
    return `Others: ${detail}`;
  });

  typeFilterLabel = computed(() => {
    const filter = this.typeFilter();
    if (!filter) return '';
    if (filter.extension) return this.otherTypeDisplay(filter.extension);
    return filter.group;
  });

  activeSearchQuery = computed(() => {
    if (this.activeTab() === 'unprocessed') return this.searchQuery();
    if (this.activeTab() === 'failed') return this.failedSearchQuery();
    return this.processedSearchQuery();
  });

  activeDocumentCount = computed(() => {
    if (this.activeTab() === 'unprocessed') return this.unprocessedDisplayCount();
    if (this.activeTab() === 'failed') return this.filteredFailed().length;
    return this.filteredProcessed().length;
  });

  unprocessedDisplayCount = computed(() => {
    if (this.searchQuery().trim() || this.typeFilter()) {
      return this.unprocessedMatchTotal();
    }
    const fromTypes = this.fileTypeCounts().reduce((sum, item) => sum + (item.count || 0), 0);
    return fromTypes > 0 ? fromTypes : this.unprocessedTotal();
  });

  sizeTypeBuckets = computed(() => {
    const tab = this.activeTab();
    if (tab === 'failed') return this.sizeFailedByType();
    if (tab === 'processed') return this.sizeProcessedByType();
    return this.sizeUnprocessedByType();
  });

  sizeTypeSegments = computed<DonutSegment[]>(() => {
    const buckets = this.sizeTypeBuckets();
    if (this.activeTab() === 'unprocessed' && !buckets.length) {
      const bytes = this.unprocessedBytes();
      if (bytes <= 0) return [];
      return this.segmentsFromOrdered(
        new Map([['Unprocessed', bytes]]),
        bytes,
        ['Unprocessed'],
        label => this.statusColor(label),
        true
      );
    }

    if (!buckets.length) return [];
    const grouped = new Map<string, number>();
    for (const item of buckets) {
      const label = this.majorFileTypeLabel(item.fileType);
      grouped.set(label, (grouped.get(label) ?? 0) + (item.sizeBytes || 0));
    }
    const total = [...grouped.values()].reduce((sum, value) => sum + value, 0);
    if (total <= 0) return [];
    return this.segmentsFromGrouped(grouped, total, true);
  });

  sizeTypeTotal = computed(() =>
    this.sizeTypeSegments().reduce((sum, item) => sum + item.sizeBytes, 0)
  );

  sizeTypeTitle = computed(() => {
    if (this.activeTab() === 'failed') return 'Failed size by type';
    if (this.activeTab() === 'processed') return 'Processed size by type';
    return 'Unprocessed size by type';
  });

  sizeTypeCaption = computed(() => {
    if (this.activeTab() === 'failed') return 'Storage among failed SharePoint documents. Expand Others to see remaining extensions.';
    if (this.activeTab() === 'processed') return 'Storage among processed SharePoint documents. Expand Others to see remaining extensions.';
    return 'Estimated mix from sampled library files, scaled to the exact unprocessed remainder. Expand Others to see remaining extensions.';
  });

  sizeOtherBreakdown = computed<OtherTypeItem[]>(() => {
    const rows = this.sizeTypeBuckets()
      .filter(item => this.majorFileTypeLabel(item.fileType) === 'Others')
      .map(item => ({
        fileType: this.normalizeExt(item.fileType) || 'other',
        count: item.count || 0,
        sizeBytes: item.sizeBytes || 0
      }))
      .sort((a, b) => b.sizeBytes - a.sizeBytes || a.fileType.localeCompare(b.fileType));

    const denom = this.sizeTypeTotal();
    const othersSum = rows.reduce((sum, row) => sum + row.sizeBytes, 0);
    return rows.map(row => ({
      fileType: row.fileType,
      label: this.otherTypeDisplay(row.fileType),
      count: row.count,
      sizeBytes: row.sizeBytes,
      percentage: denom > 0 ? Math.round((row.sizeBytes / denom) * 1000) / 10 : 0,
      share: othersSum > 0 ? Math.round((row.sizeBytes / othersSum) * 1000) / 10 : 0
    }));
  });

  statusSizeSegments = computed<DonutSegment[]>(() => {
    const grouped = new Map<string, number>([
      ['Unprocessed', this.unprocessedBytes()],
      ['Failed', this.failedBytes()],
      ['Processed', this.processedBytes()]
    ]);
    const total = [...grouped.values()].reduce((sum, value) => sum + value, 0);
    if (total <= 0) return [];
    const order = ['Unprocessed', 'Failed', 'Processed'];
    return this.segmentsFromOrdered(grouped, total, order, label => this.statusColor(label));
  });

  statusSizeTotal = computed(() =>
    this.statusSizeSegments().reduce((sum, item) => sum + item.sizeBytes, 0)
  );

  ngOnInit() {
    this.loadFileTypeCounts();
    if (this.showSizeCharts) this.loadFileSizeStats();
    this.loadUnprocessedFiles();
  }

  loadFileTypeCounts() {
    this.isLoadingFileTypes.set(true);
    this.connectorService.getSharePointFileTypeCounts().subscribe({
      next: (res) => {
        this.isLoadingFileTypes.set(false);
        const items = res.data.items || [];
        const typeSum = items.reduce((sum, item) => sum + (item.count || 0), 0);
        this.fileTypeCounts.set(items);
        this.fileTypeTotal.set(typeSum);
        if (!this.searchQuery().trim() && typeSum > 0) {
          this.unprocessedTotal.set(Math.max(this.unprocessedTotal(), typeSum));
          this.unprocessedMatchTotal.set(Math.max(this.unprocessedMatchTotal(), typeSum));
        }
      },
      error: (err) => {
        this.isLoadingFileTypes.set(false);
        this.fileTypeCounts.set([]);
        this.fileTypeTotal.set(0);
        const message = err?.error?.message || 'Failed to load SharePoint file type counts.';
        this.toast.show(message, 'error');
      }
    });
  }

  loadFileSizeStats() {
    this.isLoadingFileSizes.set(true);
    this.connectorService.getSharePointFileSizeStats().subscribe({
      next: (res) => {
        this.isLoadingFileSizes.set(false);
        const data = res.data;
        this.unprocessedBytes.set(data.unprocessedBytes || 0);
        this.failedBytes.set(data.failedBytes || 0);
        this.processedBytes.set(data.processedBytes || 0);
        this.sizeUnprocessedByType.set(data.unprocessedByType || []);
        this.sizeFailedByType.set(data.failedByType || []);
        this.sizeProcessedByType.set(data.processedByType || []);
      },
      error: (err) => {
        this.isLoadingFileSizes.set(false);
        this.unprocessedBytes.set(0);
        this.failedBytes.set(0);
        this.processedBytes.set(0);
        this.sizeUnprocessedByType.set([]);
        this.sizeFailedByType.set([]);
        this.sizeProcessedByType.set([]);
        const message = err?.error?.message || 'Failed to load SharePoint file size stats.';
        this.toast.show(message, 'error');
      }
    });
  }

  private segmentsFromCounts(items: SharePointFileTypeCountItem[], total: number): DonutSegment[] {
    if (!items.length) return [];
    const grouped = new Map<string, number>();
    for (const item of items) {
      const label = this.majorFileTypeLabel(item.fileType);
      grouped.set(label, (grouped.get(label) ?? 0) + item.count);
    }
    const groupedSum = [...grouped.values()].reduce((sum, count) => sum + count, 0);
    const denom = groupedSum > 0 ? groupedSum : total;
    if (denom <= 0) return [];
    return this.segmentsFromGrouped(grouped, denom);
  }

  private segmentsFromDocs(docs: DocumentItem[]): DonutSegment[] {
    if (!docs.length) return [];
    const grouped = new Map<string, number>();
    for (const doc of docs) {
      const label = this.majorFileTypeLabel(this.docExtension(doc));
      grouped.set(label, (grouped.get(label) ?? 0) + 1);
    }
    return this.segmentsFromGrouped(grouped, docs.length);
  }

  private segmentsFromGrouped(grouped: Map<string, number>, total: number, asSize = false): DonutSegment[] {
    const order = ['PDF', 'Word', 'Excel', 'PowerPoint', 'Others'];
    return this.segmentsFromOrdered(grouped, total, order, label => this.fileTypeColor(label), asSize);
  }

  private segmentsFromOrdered(
    grouped: Map<string, number>,
    total: number,
    order: string[],
    colorFor: (label: string) => string,
    asSize = true
  ): DonutSegment[] {
    const segments: DonutSegment[] = order
      .filter(label => (grouped.get(label) ?? 0) > 0)
      .map(label => {
        const value = grouped.get(label)!;
        return {
          label,
          count: asSize ? 0 : value,
          sizeBytes: asSize ? value : 0,
          percentage: Math.round((value / total) * 1000) / 10,
          color: colorFor(label),
          offset: 0
        };
      });

    const used = segments.reduce((sum, s) => sum + s.percentage, 0);
    if (segments.length > 0 && used !== 100 && Math.abs(100 - used) < 2) {
      const last = segments[segments.length - 1];
      last.percentage = Math.round((last.percentage + (100 - used)) * 10) / 10;
    }

    let cumulative = 0;
    for (const segment of segments) {
      segment.offset = cumulative;
      cumulative += segment.percentage;
    }
    return segments;
  }

  private docExtension(doc: DocumentItem): string {
    const ext = (doc.fileExtension || '').replace(/^\./, '');
    if (ext) return ext;
    const name = doc.fileName || '';
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot + 1) : '';
  }

  majorFileTypeLabel(fileType: string): string {
    const type = this.normalizeExt(fileType);
    if (type === 'pdf') return 'PDF';
    if (['doc', 'docx', 'docm', 'dot', 'dotx', 'rtf', 'odt'].includes(type)) return 'Word';
    if (['xls', 'xlsx', 'xlsm', 'xlsb', 'xltx', 'csv', 'ods'].includes(type)) return 'Excel';
    if (['ppt', 'pptx', 'pptm', 'ppsx', 'potx', 'odp'].includes(type)) return 'PowerPoint';
    return 'Others';
  }

  otherTypeDisplay(fileType: string): string {
    const type = this.normalizeExt(fileType);
    if (!type || type === 'other') return 'No extension';
    return '.' + type;
  }

  mixBarTitle(item: DonutSegment): string {
    if (item.label === 'Others') return this.othersTooltip();
    return `${item.label}: ${item.count}`;
  }

  mixBarSizeTitle(item: DonutSegment): string {
    return `${item.label}: ${this.formatBytes(item.sizeBytes)}`;
  }

  formatBytes(bytes: number): string {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit += 1;
    }
    const digits = unit === 0 ? 0 : value >= 10 ? 1 : 2;
    return `${value.toFixed(digits)} ${units[unit]}`;
  }

  statusColor(label: string): string {
    switch (label) {
      case 'Unprocessed': return '#64748B';
      case 'Failed': return '#EF4444';
      case 'Processed': return '#22C55E';
      default: return '#94A3B8';
    }
  }

  showOtherTypeBadge = computed(() => {
    const filter = this.typeFilter();
    return !!filter && filter.group === 'Others' && !filter.extension;
  });

  fileExtLabelFromName(name: string): string {
    const fileName = name || '';
    const dot = fileName.lastIndexOf('.');
    const ext = dot >= 0 ? fileName.slice(dot + 1) : '';
    return this.otherTypeDisplay(ext);
  }

  isGroupFilter(label: string): boolean {
    const filter = this.typeFilter();
    return !!filter && filter.group === label && !filter.extension;
  }

  isGroupInvolved(label: string): boolean {
    return this.typeFilter()?.group === label;
  }

  isExtensionFilter(fileType: string): boolean {
    return this.typeFilter()?.extension === fileType;
  }

  selectLegendType(label: string) {
    const current = this.typeFilter();
    if (current && current.group === label && !current.extension) {
      this.clearTypeFilter();
      return;
    }
    this.typeFilter.set({ group: label, extension: null });
    this.onTypeFilterChanged();
  }

  selectOtherType(fileType: string) {
    const current = this.typeFilter();
    if (current?.extension === fileType) {
      this.clearTypeFilter();
      return;
    }
    this.typeFilter.set({ group: 'Others', extension: fileType });
    this.onTypeFilterChanged();
  }

  clearTypeFilter() {
    if (!this.typeFilter()) return;
    this.typeFilter.set(null);
    this.onTypeFilterChanged();
  }

  private onTypeFilterChanged() {
    this.currentPage.set(1);
    this.failedPage.set(1);
    this.processedPage.set(1);
    this.loadUnprocessedFiles();
  }

  private applyTypeFilterToDocs(docs: DocumentItem[]): DocumentItem[] {
    const filter = this.typeFilter();
    if (!filter) return docs;
    return docs.filter(doc => this.matchesTypeFilter(this.docExtension(doc), filter));
  }

  private matchesTypeFilter(ext: string, filter: TypeFilter): boolean {
    const type = this.normalizeExt(ext);
    if (filter.extension) {
      if (filter.extension === 'other') return !type || type === 'other';
      return type === filter.extension;
    }
    return this.majorFileTypeLabel(type) === filter.group;
  }

  private unprocessedFileTypeParam(): string {
    const filter = this.typeFilter();
    if (!filter) return '';
    return filter.extension || filter.group;
  }

  private normalizeExt(fileType: string): string {
    return (fileType || '').toLowerCase().replace(/^\./, '');
  }

  fileTypeColor(label: string): string {
    switch (label) {
      case 'PDF': return '#EF4444';
      case 'Word': return '#3B82F6';
      case 'Excel': return '#22C55E';
      case 'PowerPoint': return '#F97316';
      default: return '#94A3B8';
    }
  }

  onActiveSearch(event: Event) {
    if (this.activeTab() === 'unprocessed') {
      this.onSearch(event);
      return;
    }
    if (this.activeTab() === 'failed') {
      this.onFailedSearch(event);
      return;
    }
    this.onProcessedSearch(event);
  }

  setActiveTab(tab: AnalyticsTab) {
    this.activeTab.set(tab);
    this.othersExpanded.set(false);
    this.sizeOthersExpanded.set(false);
    if (tab === 'failed' && !this.failedLoaded()) {
      this.loadStatusDocuments('Failed');
    }
    if (tab === 'processed' && !this.processedLoaded()) {
      this.loadStatusDocuments('Passed');
    }
  }

  onLegendClick(label: string) {
    if (label === 'Others' && this.otherTypeBreakdown().length > 0) {
      this.othersExpanded.update(open => !open);
      return;
    }
    this.selectLegendType(label);
  }

  onSizeLegendClick(label: string) {
    if (label === 'Unprocessed') return;
    if (label === 'Others' && this.sizeOtherBreakdown().length > 0) {
      this.sizeOthersExpanded.update(open => !open);
      return;
    }
    this.selectLegendType(label);
  }

  onStatusClick(label: string) {
    if (label === 'Unprocessed') this.setActiveTab('unprocessed');
    else if (label === 'Failed') this.setActiveTab('failed');
    else if (label === 'Processed') this.setActiveTab('processed');
  }

  isStatusActive(label: string): boolean {
    if (label === 'Unprocessed') return this.activeTab() === 'unprocessed';
    if (label === 'Failed') return this.activeTab() === 'failed';
    if (label === 'Processed') return this.activeTab() === 'processed';
    return false;
  }

  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  onSearch(event: Event) {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.currentPage.set(1);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadUnprocessedFiles(), 300);
  }

  loadUnprocessedFiles() {
    this.isLoadingUnprocessed.set(true);
    this.connectorService.getSharePointUnprocessedFiles(
      this.currentPage(),
      this.pageSize,
      this.searchQuery(),
      this.unprocessedFileTypeParam()
    ).subscribe({
      next: (res) => {
        this.isLoadingUnprocessed.set(false);
        this.allUnprocessed.set(res.data.items || []);
        const typeSum = this.fileTypeCounts().reduce((sum, item) => sum + (item.count || 0), 0);
        const apiTotal = res.data.unprocessedTotal || 0;
        if (this.searchQuery().trim() || this.typeFilter()) {
          this.unprocessedTotal.set(apiTotal);
          this.unprocessedMatchTotal.set(res.data.total || 0);
        } else {
          const shown = Math.max(apiTotal, typeSum);
          this.unprocessedTotal.set(shown);
          this.unprocessedMatchTotal.set(Math.max(res.data.total || 0, typeSum));
        }
        const page = res.data.page || 1;
        if (page !== this.currentPage()) {
          this.currentPage.set(page);
        }
      },
      error: (err) => {
        this.isLoadingUnprocessed.set(false);
        this.allUnprocessed.set([]);
        this.unprocessedTotal.set(0);
        this.unprocessedMatchTotal.set(0);
        const message = err?.error?.message || 'Failed to load unprocessed files.';
        this.toast.show(message, 'error');
      }
    });
  }

  private loadStatusDocuments(status: 'Failed' | 'Passed') {
    const isFailed = status === 'Failed';
    if (isFailed) {
      this.isLoadingFailed.set(true);
    } else {
      this.isLoadingProcessed.set(true);
    }

    const collect = (page: number, acc: DocumentItem[]) => {
      this.documentService.getDocuments(page, API_PAGE_SIZE, status).subscribe({
        next: (res) => {
          const sharePointDocs = (res.data.items || []).filter(d => !!d.sharePointItemId);
          const next = [...acc, ...sharePointDocs];
          const total = res.data.total ?? next.length;
          if (page * API_PAGE_SIZE < total) {
            collect(page + 1, next);
            return;
          }
          if (isFailed) {
            this.allFailed.set(next);
            this.failedLoaded.set(true);
            this.isLoadingFailed.set(false);
            if (this.failedPage() > this.totalFailedPages()) {
              this.failedPage.set(this.totalFailedPages());
            }
          } else {
            this.allProcessed.set(next);
            this.processedLoaded.set(true);
            this.isLoadingProcessed.set(false);
            if (this.processedPage() > this.totalProcessedPages()) {
              this.processedPage.set(this.totalProcessedPages());
            }
          }
        },
        error: (err) => {
          if (isFailed) {
            this.isLoadingFailed.set(false);
          } else {
            this.isLoadingProcessed.set(false);
          }
          const label = isFailed ? 'failed' : 'processed';
          const message = err?.error?.message || `Failed to load ${label} documents.`;
          this.toast.show(message, 'error');
        }
      });
    };

    collect(1, []);
  }

  onFailedSearch(event: Event) {
    this.failedSearchQuery.set((event.target as HTMLInputElement).value);
    this.failedPage.set(1);
  }

  onProcessedSearch(event: Event) {
    this.processedSearchQuery.set((event.target as HTMLInputElement).value);
    this.processedPage.set(1);
  }

  goToPage(page: number) {
    if (this.isLoadingUnprocessed() || page < 1 || page > this.totalPages() || page === this.currentPage()) return;
    this.currentPage.set(page);
    this.loadUnprocessedFiles();
  }

  goToFailedPage(page: number) {
    if (page < 1 || page > this.totalFailedPages()) return;
    this.failedPage.set(page);
  }

  goToProcessedPage(page: number) {
    if (page < 1 || page > this.totalProcessedPages()) return;
    this.processedPage.set(page);
  }

  paginationItems(total: number, current: number): (number | '…')[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const items: (number | '…')[] = [1];
    if (current > 3) items.push('…');

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let p = start; p <= end; p++) items.push(p);

    if (current < total - 2) items.push('…');
    items.push(total);
    return items;
  }

  fullPageNumbers(total: number): number[] {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  onPageSelect(event: Event) {
    this.goToPage(Number((event.target as HTMLSelectElement).value));
  }

  onFailedPageSelect(event: Event) {
    this.goToFailedPage(Number((event.target as HTMLSelectElement).value));
  }

  onProcessedPageSelect(event: Event) {
    this.goToProcessedPage(Number((event.target as HTMLSelectElement).value));
  }

  iconClass(item: SharePointItem): string {
    if (item.isFolder) return 'icon-folder';
    return this.fileIconClass(item.name, item.mimeType);
  }

  docIconClass(doc: DocumentItem): string {
    return this.fileIconClass(doc.fileName, doc.fileExtension);
  }

  private fileIconClass(name: string, mimeOrExt: string | null): string {
    const mime = (mimeOrExt || '').toLowerCase();
    const fileName = (name || '').toLowerCase();
    if (mime.includes('pdf') || mime === 'pdf' || fileName.endsWith('.pdf')) return 'icon-pdf';
    if (mime.includes('word') || mime.includes('document') || mime === 'doc' || mime === 'docx' || /\.docx?$/.test(fileName)) return 'icon-doc';
    if (mime.includes('sheet') || mime.includes('excel') || mime === 'xls' || mime === 'xlsx' || mime === 'csv' || /\.xlsx?$|\.csv$/.test(fileName)) return 'icon-xls';
    if (mime.includes('presentation') || mime.includes('powerpoint') || mime === 'ppt' || mime === 'pptx' || /\.pptx?$/.test(fileName)) return 'icon-ppt';
    return 'icon-default';
  }
}
